import { DEFAULT_ROLES_REQUEST, ROLES_HEADER } from '@bo-constants/roles';
import {
  DELETE_ROLE,
  DELETE_ROLE_CONTENT,
  DELETE_ROLE_LABEL
} from '@library/constants/role';
import { Table, TableBody, TableHeader } from '@library/components/table';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { Drawer } from '@library/components/navigation/Drawer';
import { EditRoleHeader } from '@bo-components/roles/editRole/EditRoleHeader';
import { EditRolePermissions } from '@bo-components/roles/editRole/EditRolePermissions';
import { EditUser } from '@bo-components/userRoles/editUser/EditUser';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { PaginationButtons } from '@library/components/buttons';
import { RolesRow } from '@library/components/table/rows/RolesRow';
import type { UpdateRolesRequest } from '@library/types/role';
import { useRoles } from '@bo-hooks/roles/useRoles';
import { useState } from 'react';

export const Roles = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [editRoleDrawerOpen, setEditRoleDrawerOpen] = useState(false);
  const [deleteRoleModalOpen, setDeleteRoleModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  const {
    isRolesLoading,
    isSelectedRoleLoading,
    rolesPagination,
    rolesWithPermissions,
    selectedRole,
    updateRole
  } = useRoles({
    rolesRequest: DEFAULT_ROLES_REQUEST,
    selectedRoleId
  });

  const roleMenuActions = {
    onEdit: (id: string) => {
      setSelectedRoleId(id);
      setEditRoleDrawerOpen(true);
    }

    // TEMP: Uncomment when _destroy attribute added to roles endpoint
    // onDelete: (id: string) => {
    //   setSelectedRoleId(id);
    //   setDeleteRoleModalOpen(true);
    // }
  };

  const handleCloseDrawer = () => {
    setEditRoleDrawerOpen(false);
    setSelectedRoleId('');
  };

  const handleCloseDeleteModal = () => {
    setDeleteRoleModalOpen(false);
    setSelectedRoleId('');
  };

  const handleEditRoleSubmit = async (role: UpdateRolesRequest) => {
    await updateRole({
      id: String(role.id),
      name: role.name,
      description: role.description
    })
      .then(() => {
        handleCloseDrawer();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
  };

  const handleDeleteRoleConfirmation = () => {
    // TODO: Implement delete role API call
  };

  if (isRolesLoading) {
    return <Loading loadingMessage="Loading Roles" />;
  }

  if (!rolesWithPermissions?.length) {
    return (
      <NoResults label="Use the search bar to search by Role or Description" />
    );
  }

  return (
    <>
      <Table>
        <div className="flex flex-col overflow-x-auto" data-testid="Roles">
          <TableHeader headers={ROLES_HEADER} />
          <TableBody>
            {rolesWithPermissions.map((role) => (
              <RolesRow
                key={role.id}
                role={role}
                menuActions={roleMenuActions}
              />
            ))}
          </TableBody>
        </div>

        <PaginationButtons
          currentPage={currentPage}
          handlePageChange={setCurrentPage}
          totalPages={rolesPagination?.total_pages ?? 0}
        />
      </Table>

      <Drawer
        isOpen={editRoleDrawerOpen}
        onCloseDrawer={handleCloseDrawer}
        direction="right"
      >
        {!isSelectedRoleLoading && !!selectedRole ? (
          <EditUser>
            <EditRoleHeader role={selectedRole} onClose={handleCloseDrawer} />
            <EditRolePermissions
              enabledPermissionIds={selectedRole.permissionIds}
              onClose={() => setEditRoleDrawerOpen(false)}
              onSave={handleEditRoleSubmit}
              role={selectedRole}
            />
          </EditUser>
        ) : (
          <div className="flex h-72 justify-center">
            <Loading loadingMessage="Loading Role Details" />
          </div>
        )}
      </Drawer>

      <ConfirmationModal
        isOpen={deleteRoleModalOpen}
        confirmationCopy={DELETE_ROLE}
        onCancelModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteRoleConfirmation}
        label={DELETE_ROLE_LABEL}
      >
        {DELETE_ROLE_CONTENT}
      </ConfirmationModal>
    </>
  );
};
